import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { getKeyScopeImpact, getMyCredential, testDrive, updateAuth, updateIntegration, type InstallerCredentialStatus, type InstallerKeyScope, type KeyScopeImpact } from './apiIntegrationService';
import { AuthType, EndpointParam, Integration, IntegrationDetail, TestDriveResult } from './apiIntegrations.types';
import { methodClass } from './IntegrationDetailBody';
import './apiintegrations.css';

export interface ConnectSettings {
  baseUrl: string;
  authType: AuthType;
  location: 'header' | 'query';
  paramName: string;
  /** The editor's own key, only what they typed this session - never a saved one. */
  secret: string;
  /** Who enters the key when someone else installs it. */
  keyScope: InstallerKeyScope;
}

export function connectSettingsFrom(integration: Integration): ConnectSettings {
  return {
    baseUrl: integration.base_url || '',
    authType: integration.auth_type,
    location: integration.auth_location || 'header',
    paramName: integration.auth_param_name || '',
    secret: '',
    keyScope: integration.installer_key_scope ?? 'agency',
  };
}

/** The auth block save and Test Drive send. secret is the editor's own key (omitted when blank = keep it). */
export function authPayload(s: ConnectSettings) {
  return {
    auth_type: s.authType,
    location: s.authType === 'api_key' ? s.location : null,
    param_name: s.authType === 'api_key' ? (s.paramName.trim() || null) : null,
    secret: s.secret || undefined,
  };
}

export function connectProblem(s: ConnectSettings, integration: Integration): string | null {
  if (!s.baseUrl.trim()) return 'Enter the API base URL.';
  if (!/^https?:\/\//i.test(s.baseUrl.trim())) return 'The base URL must start with http:// or https://.';
  if (s.authType === 'api_key' && !s.paramName.trim()) return 'Enter the header or query param name for the API key.';
  return null;
}

const MAX_BODY_CHARS = 3000;

/** Test Drive inputs are text boxes; send each value as its param's schema type. */
function coerce(param: EndpointParam, raw: string): unknown {
  const value = raw.trim();
  switch (param.type) {
    case 'integer':
    case 'number': {
      const n = Number(value);
      if (Number.isNaN(n)) throw new Error(`${param.name} must be a number.`);
      return param.type === 'integer' ? Math.trunc(n) : n;
    }
    case 'boolean':
      if (!['true', 'false'].includes(value.toLowerCase())) throw new Error(`${param.name} must be true or false.`);
      return value.toLowerCase() === 'true';
    case 'array':
    case 'object':
      try {
        return JSON.parse(value);
      } catch {
        throw new Error(`${param.name} must be valid JSON.`);
      }
    default:
      return raw;
  }
}

const TestResultCard: React.FC<{ result: TestDriveResult }> = ({ result }) => {
  const passed = !result.error && result.status_code !== null && result.status_code >= 200 && result.status_code < 300;
  const body = typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2);
  const trimmed = body && body.length > MAX_BODY_CHARS ? `${body.slice(0, MAX_BODY_CHARS)}\n…` : body;
  return (
    <div className={`ai-result ${passed ? 'ai-result--pass' : 'ai-result--fail'}`} role="status">
      <div className="ai-result__head">
        <span className={passed ? 'ai-result__verdict--pass' : 'ai-result__verdict--fail'}>{passed ? '✓ Passed' : '✕ Failed'}</span>
        {result.status_code !== null && <span>HTTP {result.status_code}</span>}
        {result.latency_ms !== null && <span className="ai-mono">{result.latency_ms} ms</span>}
      </div>
      {result.error && <p className="ai-error" style={{ margin: 0 }}>{result.error}</p>}
      {result.note && <p className="ai-hint">{result.note}</p>}
      {!result.error && trimmed && <pre className="ai-code-box">{trimmed}</pre>}
    </div>
  );
};

interface Props {
  detail: IntegrationDetail;
  settings: ConnectSettings;
  onSettingsChange: (s: ConnectSettings) => void;
  onDetailChange: (d: IntegrationDetail) => void;
  onBack: () => void;
  onNext: () => void;
}

const ConnectStep: React.FC<Props> = ({ detail, settings, onSettingsChange, onDetailChange, onBack, onNext }) => {
  const { integration, endpoints } = detail;
  const [testEndpointId, setTestEndpointId] = useState<number | ''>(endpoints[0]?.id ?? '');
  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestDriveResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const testEndpoint = endpoints.find(e => e.id === testEndpointId);
  const problem = connectProblem(settings, integration);
  const set = (patch: Partial<ConnectSettings>) => onSettingsChange({ ...settings, ...patch });
  // The editor's own saved key, if any - shown as its last 4 only.
  const [myKey, setMyKey] = useState<InstallerCredentialStatus | null>(null);
  const loadMyKey = useCallback(() => {
    getMyCredential(integration.id).then(setMyKey).catch(() => setMyKey(null));
  }, [integration.id]);
  useEffect(() => { loadMyKey(); }, [loadMyKey]);
  const mySavedLast4 = myKey?.set && myKey.source === 'you' ? myKey.last4 : null;

  // Changing who enters the key on a published integration removes the keys saved the
  // old way - say how many, and who'll need to add one, before it's saved.
  const savedScope: InstallerKeyScope = integration.installer_key_scope ?? 'agency';
  const scopeChanging = integration.status === 'published' && settings.authType !== 'none' && settings.keyScope !== savedScope;
  const [scopeImpact, setScopeImpact] = useState<KeyScopeImpact | null>(null);
  useEffect(() => {
    setScopeImpact(null);
    if (!scopeChanging) return;
    let cancelled = false;
    getKeyScopeImpact(integration.id, settings.keyScope)
      .then(impact => { if (!cancelled) setScopeImpact(impact); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [scopeChanging, integration.id, settings.keyScope]);

  // Test Drive calls through the saved definition, so save what's on screen first -
  // the result then reflects exactly what the user sees.
  const runTest = async () => {
    if (!testEndpoint) return;
    setTestError(null);
    setTestResult(null);
    if (problem) {
      setTestError(problem);
      return;
    }
    let values: Record<string, unknown>;
    try {
      values = {};
      for (const p of testEndpoint.params) {
        const raw = testValues[p.name];
        if (raw !== undefined && raw !== '') values[p.name] = coerce(p, raw);
      }
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Check the test values.');
      return;
    }
    setTesting(true);
    try {
      await updateIntegration(integration.id, { base_url: settings.baseUrl.trim() });
      const saved = await updateAuth(integration.id, authPayload(settings)) as Integration;
      onDetailChange({ ...detail, integration: { ...integration, ...saved } });
      if (settings.secret) {
        set({ secret: '' });
        loadMyKey();
      }
      setTestResult(await testDrive(integration.id, testEndpoint.id, values));
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Test Drive failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <motion.div className="mt-step-content" key="integration-connect"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }}>
      <h2 className="mt-step-title">Connect</h2>
      <p className="mt-step-subtitle">Tell us where the API lives and how to sign in to it, then run a real test call.</p>

      <section className="ai-form-section">
        <h3 className="ai-form-section__title">Where the API lives</h3>
        <div className="mt-field mt-field--full">
          <label className="mt-label" htmlFor="integration-base-url">Base URL <span className="mt-required">*</span></label>
          <input id="integration-base-url" className="mt-input" value={settings.baseUrl}
            onChange={e => set({ baseUrl: e.target.value })} placeholder="https://api.example.com" />
          <p className="mt-field-hint">Every endpoint's path is added to this.</p>
        </div>
      </section>

      <section className="ai-form-section">
        <h3 className="ai-form-section__title">How it signs in</h3>
        <div className="mt-field-row">
          <div className="mt-field mt-field--grow">
            <label className="mt-label" htmlFor="integration-auth-type">Authentication</label>
            <select id="integration-auth-type" className="mt-select" value={settings.authType}
              onChange={e => set({ authType: e.target.value as AuthType })}>
              <option value="none">None</option>
              <option value="api_key">API key</option>
              <option value="bearer">Bearer token</option>
            </select>
          </div>
          {settings.authType === 'api_key' && (
            <div className="mt-field">
              <label className="mt-label" htmlFor="integration-auth-location">Sent in</label>
              <select id="integration-auth-location" className="mt-select" value={settings.location}
                onChange={e => set({ location: e.target.value as 'header' | 'query' })}>
                <option value="header">Header</option>
                <option value="query">Query string</option>
              </select>
            </div>
          )}
        </div>
        {settings.authType === 'api_key' && (
          <div className="mt-field mt-field--full">
            <label className="mt-label" htmlFor="integration-auth-param">{settings.location === 'query' ? 'Query param name' : 'Header name'} <span className="mt-required">*</span></label>
            <input id="integration-auth-param" className="mt-input" value={settings.paramName}
              onChange={e => set({ paramName: e.target.value })} placeholder="X-API-Key" />
          </div>
        )}
        {settings.authType === 'none' && <p className="mt-field-hint">This API doesn't need a key.</p>}
      </section>

      {settings.authType !== 'none' && (
        <section className="ai-form-section">
          <h3 className="ai-form-section__title">API keys</h3>
          <fieldset className="ai-fieldset">
            <legend className="mt-label">When someone installs this, who enters their API key?</legend>
            <div className="ai-choice-group">
              <label className="ai-choice">
                <input type="radio" name="installer-key-scope" checked={settings.keyScope === 'agency'} onChange={() => set({ keyScope: 'agency' })} />
                <span><strong>One key per agency.</strong> An Agency Admin enters it when they install it for their agency.</span>
              </label>
              <label className="ai-choice">
                <input type="radio" name="installer-key-scope" checked={settings.keyScope === 'per_user'} onChange={() => set({ keyScope: 'per_user' })} />
                <span><strong>Each person enters their own.</strong> Anyone can install it and add their own key.</span>
              </label>
            </div>
          </fieldset>
          {scopeChanging && (
            <div className="ai-callout ai-callout--warn" role="note">
              {settings.keyScope === 'per_user'
                ? "Saving this removes every agency's key for this integration. Everyone who has it through their agency will need to add their own key."
                : "Saving this removes the personal keys of everyone who has it through their agency - their calls will use the agency's key, which an Agency Admin adds from the Installed tab."}
              {scopeImpact && scopeImpact.keys_removed > 0 && (
                <> {scopeImpact.keys_removed} saved {scopeImpact.keys_removed === 1 ? 'key' : 'keys'} will be removed.</>
              )}
              {scopeImpact && scopeImpact.people_needing_key > 0 && (
                <> {scopeImpact.people_needing_key} {scopeImpact.people_needing_key === 1 ? 'person' : 'people'} won't have a key until one is added.</>
              )}
            </div>
          )}
          <div className="mt-field mt-field--full">
            <label className="mt-label" htmlFor="integration-secret">
              Your {settings.authType === 'bearer' ? 'bearer token' : 'API key'}
            </label>
            <input id="integration-secret" type="password" autoComplete="off" className="mt-input" value={settings.secret}
              onChange={e => set({ secret: e.target.value })}
              placeholder={mySavedLast4 ? `Saved (••••${mySavedLast4}) - type to replace` : 'Paste your own key'} />
            <p className="mt-field-hint">
              Only used for your own calls and Test Drive - never shared with or used by anyone who installs this.
              Stored encrypted; only the last 4 characters are ever shown.
            </p>
          </div>
        </section>
      )}

      <section className="ai-form-section">
      <h3 className="ai-form-section__title">Test Drive</h3>
      <p className="ai-hint">Makes a real call with the settings above and your own key (they're saved first).</p>
      <div className="mt-field mt-field--full">
        <label className="mt-label" htmlFor="integration-test-endpoint">Endpoint</label>
        <select id="integration-test-endpoint" className="mt-select" value={testEndpointId}
          onChange={e => { setTestEndpointId(Number(e.target.value)); setTestValues({}); setTestResult(null); setTestError(null); }}>
          {endpoints.map(e => <option key={e.id} value={e.id}>{e.http_method} {e.name}</option>)}
        </select>
        {testEndpoint && (
          <p className="mt-field-hint">
            <span className={methodClass(testEndpoint.http_method)}>{testEndpoint.http_method}</span>{' '}
            <span className="ai-mono">{testEndpoint.path_template}</span>
          </p>
        )}
      </div>
      {testEndpoint?.params.map(p => (
        <div className="mt-field mt-field--full" key={p.name}>
          <label className="mt-label" htmlFor={`test-${p.name}`}>
            {p.name} <span className="ai-mono">({p.type}, {p.location})</span>{p.required && <> <span className="mt-required">*</span></>}
          </label>
          <input id={`test-${p.name}`} className="mt-input" value={testValues[p.name] || ''}
            placeholder={p.type === 'boolean' ? 'true or false' : p.type === 'array' || p.type === 'object' ? 'JSON' : p.description || ''}
            onChange={e => setTestValues({ ...testValues, [p.name]: e.target.value })} />
        </div>
      ))}
      <div>
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" disabled={!testEndpoint || testing} onClick={runTest}>
          {testing ? 'Running…' : 'Run test'}
        </button>
      </div>
      {testError && <p className="ai-error" role="alert">{testError}</p>}
      {testResult && <TestResultCard result={testResult} />}
      </section>

      <div className="mt-actions">
        <button type="button" className="mt-btn mt-btn--secondary" onClick={onBack}>← Back</button>
        <button type="button" className="mt-btn mt-btn--primary" disabled={!!problem} onClick={onNext}>Next →</button>
        {problem && <span className="mt-field-hint">{problem}</span>}
      </div>
    </motion.div>
  );
};

export default ConnectStep;
